import React, { useState, useEffect } from "react";

const DEFAULT_CONFIG = {
  overhead:     { pct: 10, enabled: true },
  accumulation: { pct: 8,  enabled: true },
  unforeseen:   { pct: 5,  enabled: true },
  vat:          { pct: 18, enabled: true },
  extra: [],
};

function deepMerge(base, override) {
  if (!override) return base;
  const result = { ...base };
  for (const key of ["overhead", "accumulation", "unforeseen", "vat"]) {
    result[key] = { ...base[key], ...(override[key] || {}) };
  }
  result.extra = override.extra || [];
  return result;
}

const FIELD_META = {
  overhead:     { geo: "ზედნადები ხარჯები", eng: "Overhead" },
  accumulation: { geo: "გეგმიური დაგროვება", eng: "Accumulation" },
  unforeseen:   { geo: "გაუთვალისწინებელი ხარჯები", eng: "Unforeseen" },
  vat:          { geo: "დღგ", eng: "VAT" },
};

// Which sheet each markup lands on — surfaced as a caption so the user knows
// where the change shows up in Excel.
const GROUPS = [
  {
    caption: "დეტალურ ფურცლებზე / on detail (child) sheets",
    keys: ["overhead", "accumulation"],
  },
  {
    caption: "TOTAL ფურცელზე / on the TOTAL sheet",
    keys: ["unforeseen", "vat"],
  },
];

export default function ParametersTab({ project, onSave, onGoToBOQ }) {
  const [cfg, setCfg] = useState(() => deepMerge(DEFAULT_CONFIG, project?.cost_config));
  const [newExtraName, setNewExtraName] = useState("");
  const [newExtraPct, setNewExtraPct] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setCfg(deepMerge(DEFAULT_CONFIG, project?.cost_config));
    setSaved(false);
  }, [project?.id, project?.cost_config]);

  const setField = (key, field, value) => {
    setSaved(false);
    setCfg((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const addExtra = () => {
    const name = newExtraName.trim();
    if (!name) return;
    const pct = parseFloat(newExtraPct) || 0;
    setSaved(false);
    setCfg((prev) => ({
      ...prev,
      extra: [...(prev.extra || []), { name, pct, enabled: true }],
    }));
    setNewExtraName("");
    setNewExtraPct(0);
  };

  const removeExtra = (idx) => {
    setSaved(false);
    setCfg((prev) => ({ ...prev, extra: prev.extra.filter((_, i) => i !== idx) }));
  };

  const updateExtra = (idx, field, value) => {
    setSaved(false);
    setCfg((prev) => {
      const extra = prev.extra.map((e, i) => (i === idx ? { ...e, [field]: value } : e));
      return { ...prev, extra };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await onSave(cfg);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (key) => {
    const meta = FIELD_META[key];
    const row = cfg[key];
    return (
      <label key={key} className={"params-row" + (row.enabled ? "" : " is-off")}>
        <input
          type="checkbox"
          className="params-check"
          checked={row.enabled}
          onChange={(e) => setField(key, "enabled", e.target.checked)}
        />
        <span className="params-label">
          {meta.geo}
          <span className="params-label-en"> {meta.eng}</span>
        </span>
        <span className="params-pct">
          <input
            type="number"
            className="params-num"
            min={0}
            max={100}
            step={0.5}
            value={row.pct}
            disabled={!row.enabled}
            onChange={(e) => setField(key, "pct", parseFloat(e.target.value) || 0)}
          />
          <span className="params-sign">%</span>
        </span>
      </label>
    );
  };

  return (
    <div className="params-tab">
      <div className="params-head">
        <div className="params-title">პარამეტრები / Cost parameters</div>
        <div className="params-subtitle">
          ზედნადები, დაგროვება, გაუთვალისწინებელი ხარჯები და დღგ — გამოიყენება Excel-ის ფურცლების ფორმულებში.
        </div>
      </div>

      {GROUPS.map((group) => (
        <div key={group.caption} className="params-group">
          <div className="params-group-caption">{group.caption}</div>
          {group.keys.map(renderRow)}
        </div>
      ))}

      <div className="params-group">
        <div className="params-group-caption">დამატებითი ხარჯები / extra costs</div>

        {(cfg.extra || []).length === 0 && (
          <div className="params-empty">დამატებითი ხარჯები არ არის.</div>
        )}

        {(cfg.extra || []).map((item, idx) => (
          <div key={idx} className={"params-row" + (item.enabled ? "" : " is-off")}>
            <input
              type="checkbox"
              className="params-check"
              checked={item.enabled}
              onChange={(e) => updateExtra(idx, "enabled", e.target.checked)}
            />
            <input
              type="text"
              className="params-text"
              value={item.name}
              onChange={(e) => updateExtra(idx, "name", e.target.value)}
              placeholder="ხარჯის სახელი"
            />
            <span className="params-pct">
              <input
                type="number"
                className="params-num"
                min={0}
                max={100}
                step={0.5}
                value={item.pct}
                disabled={!item.enabled}
                onChange={(e) => updateExtra(idx, "pct", parseFloat(e.target.value) || 0)}
              />
              <span className="params-sign">%</span>
            </span>
            <button
              type="button"
              className="icon-btn icon-btn--danger"
              onClick={() => removeExtra(idx)}
              title="წაშლა / Remove"
            >
              ✕
            </button>
          </div>
        ))}

        <div className="params-add-row">
          <input
            type="text"
            className="params-text"
            placeholder="ახალი ხარჯის სახელი…"
            value={newExtraName}
            onChange={(e) => setNewExtraName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addExtra()}
          />
          <span className="params-pct">
            <input
              type="number"
              className="params-num"
              value={newExtraPct}
              onChange={(e) => setNewExtraPct(e.target.value)}
              min={0}
              max={100}
              step={0.5}
            />
            <span className="params-sign">%</span>
          </span>
          <button type="button" className="boq-action-btn" onClick={addExtra} disabled={!newExtraName.trim()}>
            + დამატება
          </button>
        </div>
      </div>

      <button
        type="button"
        className="primary params-save"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? "მიმდინარეობს…" : "💾 შენახვა"}
      </button>

      {saved && (
        <div className="params-saved">
          ✓ შენახულია. Excel-ში სანახავად გადადით <strong>BOQ</strong> ჩანართზე და დააჭირეთ{" "}
          <strong>Load (Server → Excel)</strong>.
          {onGoToBOQ && (
            <button type="button" className="boq-action-btn params-saved-goto" onClick={onGoToBOQ}>
              BOQ →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
